// ERP API Configuration
// URL padrão para DEV local (opcional).
// Em DEV (localhost/127.0.0.1) a prioridade é:
// 1) env: VITE_API_BASE_URL
// 2) localStorage: api_base_url (somente se fonte=manual)
// 3) constante abaixo (edite aqui)
// 4) origin atual
// Em produção, a prioridade continua sendo localStorage > origin.
const LOCAL_DEV_API_BASE_URL = 'https://localhost:44317';

type ApiBaseUrlSource = 'manual' | 'auto' | 'env' | 'origin' | 'default';

const API_BASE_URL_STORAGE_KEY = 'api_base_url';
const API_BASE_URL_SOURCE_STORAGE_KEY = 'api_base_url_source';

const normalizeBaseUrl = (value: string) => {
  const trimmed = (value || '').trim();
  if (!trimmed) return '';
  return trimmed.endsWith('/') ? trimmed.slice(0, -1) : trimmed;
};

export const API_CONFIG = {
  baseURL: '', // Will be filled dynamically with user configuration
  apiKey: '', // Will be filled dynamically with user configuration
  token: '', // Added token property for HeaderToken
  timeout: 30000, // Timeout in ms for requests
  useCorsProxy: false, // Option to use CORS proxy
  corsProxyURL: 'https://corsproxy.io/?', // CORS proxy URL
  baseUrlSource: 'auto' as ApiBaseUrlSource,
  
  // Endpoints
  endpoints: {
    // Autenticação
    auth: {
      login: '/api/PessoaVendedors/LogarApp',
    },
    // Produtos
    products: {
      list: '/api/GenericIntegrations/GetProdutos',
      byId: '/api/GenericIntegrations/GetProduto/:id',
      sync: '/api/GenericIntegrations/GetProdutos', // Endpoint para sincronização completa
    },
    // Categorias
    categories: {
      list: '/api/GenericIntegrations/Categorias',
      byId: '/api/GenericIntegrations/Categoria/:id',
    },
    // Pedidos
    orders: {
      list: '/api/pedidos',
      create: '/api/pedidos',
      update: '/api/pedidos/:id',
      byId: '/api/pedidos/:id',
      sync: '/api/pedidos/sincronizar', // Endpoint para sincronização completa
      syncOrder: '/api/vendas/SalvarOuAtualizarVendaApp', // Novo endpoint para sincronização de vendas
      getComplete: '/api/Vendas/ObterPedidoCompleto', // GET ?vendaID=<id>
      getPreparoStatus: '/api/Vendas/PreparoStatus', // GET ?vendaID=<id>
      setPreparoStatus: '/api/Vendas/PreparoStatus', // POST body
      getEntregaStatus: '/api/Vendas/EntregaStatus', // GET ?vendaID=<id>
      setEntregaStatus: '/api/Vendas/EntregaStatus', // POST body
      deliveryBoard: '/api/Vendas/DeliveryBoard',
      mesasAbertas: '/api/Vendas/MesasAbertas',
    },
    // Formas de Pagamento
    paymentMethods: {
      list: '/api/FormaPagamentoes',
      byId: '/api/FormaPagamentoes/:id',
    },
    // Usuários/Funcionários
    users: {
      list: '/api/usuarios',
      byId: '/api/usuarios/:id',
      authenticate: '/api/usuarios/autenticar',
      sync: '/api/usuarios/sincronizar', // Endpoint para sincronização completa
    },
    // Add customers endpoints
    customers: {
      list: '/api/PessoaClientesApi',
      byId: '/api/PessoaClientesApi/:id'
    }
  },
  
  // Domínios permitidos (opcional). NÃO deve ser fixo por cliente.
  // Pode ser configurado via:
  // - localStorage: api_allowed_domains (JSON array de strings)
  // - env (Vite): VITE_API_ALLOWED_DOMAINS (JSON array)
  allowedDomains: [] as string[],

  // Heurística padrão para rodar no mesmo host/ambiente
  getDefaultBaseUrl: function() {
    const currentOrigin = window.location.origin;
    return currentOrigin.replace('/mobile', '');
  },
  
  // Get full URL based on current domain
  getFullUrl: function(endpoint: string) {
    // Se já temos uma URL base completa, usar ela
    if (this.baseURL) {
      return `${this.baseURL}${endpoint}`;
    }
    
    // Tentar criar uma URL usando o domínio atual
    const currentDomain = window.location.hostname;
    
    // Verificar se estamos em ambiente de desenvolvimento local
    const isDevelopment = currentDomain === 'localhost' || currentDomain.includes('127.0.0.1');
    
    // Se estamos em um subdiretório (ex.: app hospedada sob /restaurante/),
    // preferimos endpoint relativo para evitar CORS no mesmo host.
    const isInSubdirectory = window.location.pathname.includes('/restaurante/');
    if (isInSubdirectory) {
      return endpoint;
    }

    // Se houver lista de domínios permitidos configurada, use-a (sem defaults por cliente)
    for (const domain of this.allowedDomains) {
      if (currentDomain.includes(domain) || domain.includes(currentDomain)) {
        const protocol = window.location.protocol;
        return `${protocol}//${domain}${endpoint}`;
      }
    }
    
    // Em desenvolvimento local, se não tiver URL base configurada, não podemos fazer requests
    if (isDevelopment && !this.baseURL) {
      console.warn('API URL não configurada em ambiente de desenvolvimento local. Configure em Configurações > API.');
    }
    
    // Fallback genérico: origem atual
    return `${this.getDefaultBaseUrl()}${endpoint}`;
  },
  
  // Initialize API with settings
  initialize: function() {
    try {
      // Carrega allowedDomains de localStorage/env (opcional)
      try {
        const fromStorage = localStorage.getItem('api_allowed_domains');
        const fromEnv = (import.meta as any)?.env?.VITE_API_ALLOWED_DOMAINS;
        const raw = (fromStorage && fromStorage.trim()) || (fromEnv && String(fromEnv).trim()) || '';
        if (raw) {
          const parsed = JSON.parse(raw);
          this.allowedDomains = Array.isArray(parsed) ? parsed.filter(Boolean) : [];
        } else {
          this.allowedDomains = [];
        }
      } catch {
        this.allowedDomains = [];
      }

      // Base URL persistida (quando configurada manualmente em Configurações > API)
      const persistedBaseURL = normalizeBaseUrl(localStorage.getItem(API_BASE_URL_STORAGE_KEY) || '');
      const persistedSource = (localStorage.getItem(API_BASE_URL_SOURCE_STORAGE_KEY) || '').trim() as ApiBaseUrlSource;

      const currentDomain = window.location.hostname;
      const isDevelopment = currentDomain === 'localhost' || currentDomain.includes('127.0.0.1');

      // (Opcional) URL via env do Vite
      const envBaseUrl = normalizeBaseUrl(String((import.meta as any)?.env?.VITE_API_BASE_URL || ''));

      const defaultBaseUrl = normalizeBaseUrl(this.getDefaultBaseUrl());

      // Resolve baseURL efetivo
      let effectiveBaseUrl = '';
      let effectiveSource: ApiBaseUrlSource = 'auto';

      if (isDevelopment) {
        // DEV: env > (persistido manual) > constante > fallback origin
        if (envBaseUrl) {
          effectiveBaseUrl = envBaseUrl;
          effectiveSource = 'env';
        } else if (persistedBaseURL && persistedSource === 'manual') {
          effectiveBaseUrl = persistedBaseURL;
          effectiveSource = 'manual';
        } else if (LOCAL_DEV_API_BASE_URL) {
          effectiveBaseUrl = normalizeBaseUrl(LOCAL_DEV_API_BASE_URL);
          effectiveSource = 'default';
        } else if (persistedBaseURL) {
          // Se existir um valor antigo mas não marcado como manual, usa como último recurso
          effectiveBaseUrl = persistedBaseURL;
          effectiveSource = persistedSource || 'auto';
        } else {
          effectiveBaseUrl = defaultBaseUrl;
          effectiveSource = 'origin';
        }
      } else {
        // PROD: (persistido) > origin
        if (persistedBaseURL) {
          effectiveBaseUrl = persistedBaseURL;
          effectiveSource = persistedSource || 'manual';
        } else {
          effectiveBaseUrl = defaultBaseUrl;
          effectiveSource = 'origin';
        }
      }

      this.baseURL = normalizeBaseUrl(effectiveBaseUrl);
      this.baseUrlSource = effectiveSource;

      // Só persistir automaticamente se NÃO houver URL persistida ainda.
      // Isso evita "grudar" ou sobrescrever um valor manual.
      if (!persistedBaseURL && this.baseURL && effectiveSource !== 'env') {
        localStorage.setItem(API_BASE_URL_STORAGE_KEY, this.baseURL);
        localStorage.setItem(API_BASE_URL_SOURCE_STORAGE_KEY, 'auto');
        console.log('API URL definida automaticamente:', this.baseURL);
      }

      console.log('API_CONFIG initializing with base URL:', this.baseURL);
      
      // IMPORTANT: Check for HeaderToken first (the one used in C# example), then fallback to other tokens
      this.apiKey = localStorage.getItem('HeaderToken') || '';
      if (!this.apiKey) {
        this.apiKey = localStorage.getItem('api_auth_token') || '';
      }
      if (!this.apiKey) {
        this.apiKey = localStorage.getItem('api_key') || '';
      }
      
      // Set the token property for consistency
      this.token = this.apiKey;
      
      // Decrypt the token if it's encrypted (base64)
      if (this.apiKey) {
        try {
          this.apiKey = atob(this.apiKey);
          this.token = this.apiKey; // Also update token with decrypted value
          console.log('API token loaded and decrypted successfully, length:', this.apiKey.length);
        } catch (e) {
          // If decryption fails, it might not be encrypted
          console.log('API token decryption failed, using as-is');
        }
      }
      
      this.useCorsProxy = localStorage.getItem('use_cors_proxy') === 'true';
      
      // Verify authentication tokens available
      if (!this.apiKey) {
        console.warn('No API token found. API requests may fail.');
      } else {
        console.log('API token loaded successfully, length:', this.apiKey.length);
      }
      
      // Try to encode the URL to prevent pattern-matching errors
      if (this.baseURL) {
        try {
          // Test if the URL is valid
          new URL(this.baseURL);
        } catch (error) {
          console.error('Invalid API URL format:', this.baseURL);
          this.baseURL = ''; // Reset to prevent errors
        }
      }
      
      // Detecção genérica de subdiretório: se a app estiver sob /restaurante/,
      // usar endpoints relativos (mesmo host) e desabilitar proxy.
      const isInSubdirectory = window.location.pathname.includes('/restaurante/');
      if (isInSubdirectory) {
        this.baseURL = '';
        this.baseUrlSource = 'origin';
        this.useCorsProxy = false;
        console.log('Detectado subdiretório (/restaurante/): usando URLs relativas');
      }
      
      console.log('API Config initialized:', {
        baseURL: this.baseURL,
        hasApiKey: !!this.apiKey,
        tokenLength: this.apiKey ? this.apiKey.length : 0,
        useCorsProxy: this.useCorsProxy,
        path: window.location.pathname,
        domain: window.location.hostname
      });
      
      return { 
        baseURL: this.baseURL, 
        apiKey: this.apiKey,
        token: this.token,
        useCorsProxy: this.useCorsProxy 
      };
    } catch (error) {
      console.error('Error initializing API configuration:', error);
      return { baseURL: '', apiKey: '', token: '', useCorsProxy: false };
    }
  },

  // Fonte única para leitura do baseURL em runtime
  getBaseURL: function() {
    return this.baseURL || this.initialize().baseURL;
  },

  // Somente para telas de configuração (mostrar o que está persistido)
  getPersistedBaseURL: function() {
    return normalizeBaseUrl(localStorage.getItem(API_BASE_URL_STORAGE_KEY) || '');
  },

  setBaseURL: function(baseURL: string, source: ApiBaseUrlSource = 'manual', persist = true) {
    const normalized = normalizeBaseUrl(baseURL);
    this.baseURL = normalized;
    this.baseUrlSource = source;

    if (persist) {
      if (normalized) {
        localStorage.setItem(API_BASE_URL_STORAGE_KEY, normalized);
        localStorage.setItem(API_BASE_URL_SOURCE_STORAGE_KEY, source);
      } else {
        localStorage.removeItem(API_BASE_URL_STORAGE_KEY);
        localStorage.removeItem(API_BASE_URL_SOURCE_STORAGE_KEY);
      }
    }

    return normalized;
  },
};
